import { useRichText } from "@hooks/useRichText";
import { type ComponentProps, type FromSanityGraphQL } from "@ts/components";
import { type AdditionalContent, type SectionAccordion } from "@ts/sanity";
import React, { useState, type FC, type ReactNode } from "react";

type AccordionInputProps = ComponentProps & FromSanityGraphQL<SectionAccordion>;

type AccordionOutputProps = AccordionInputProps & {
  handleClick: (index: number) => () => void;
  active: number | null;
  contents?: (AdditionalContent & {
    content: ReactNode;
  })[];
};

export const withAccordion =
  (Component: FC<AccordionOutputProps>) =>
  ({
    name = "Accordion",
    additionalContents,
    analytics,
    ...rest
  }: AccordionInputProps) => {
    const { parseContent } = useRichText();
    const [active, setActive] = useState<number | null>(null);

    const mappedContents = additionalContents?.map((content) => ({
      ...content,
      content: parseContent(content?.content?.content),
    }));

    const handleClick = (index: number) => () => {
      if (index === active) {
        setActive(null);
      } else {
        setActive(index);
      }
    };

    Component.displayName = name;
    return (
      <Component
        contents={mappedContents}
        active={active}
        handleClick={handleClick}
        {...rest}
      />
    );
  };
